import React, { useEffect, useRef, useState } from "react";
import jsQR from "jsqr";
import api from "../../services/api";

/**
 * Вкладка «Сканер»: перенос вашей текущей логики.
 * printerAccordion — уже готовый блок сверху (IP/Name + тест).
 */
const ScannerTab = ({ hasRole, printerAccordion }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const [error, setError] = useState("");
  const [result, setResult] = useState("");
  const [prevScans, setPrevScans] = useState([]);
  const [orderId, setOrderId] = useState(null);
  const [scanResponse, setScanResponse] = useState(null);
  const [items, setItems] = useState([]);
  const [scanedItemsIds, setScanedItemsIds] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const [isPrintCompleted, setIsPrintCompleted] = useState(false);
  const [isScanCompleted, setIsScanCompleted] = useState(false);

  // камера включается только на вкладке «Сканер» (родитель просто рендерит этот компонент)
  useEffect(() => {
    const abortController = new AbortController();

    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia(
          { video: { facingMode: "environment" } },
          { signal: abortController.signal }
        );
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Ошибка доступа к камере:", err);
          setError(`Ошибка: ${err.message}`);
        }
      }
    };

    startCamera();

    return () => {
      abortController.abort();
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject;
        const tracks = stream.getTracks();
        tracks.forEach((t) => t.stop());
      }
    };
  }, []);

  const handleScannedCode = async (code) => {
    const payload = { OrderId: orderId, PrevScans: prevScans, NewScan: code };

    try {
      const response = await api.post("/scan", payload);
      setScanResponse(response.data);

      if (orderId !== response.data.orderId) {
        setOrderId(response.data.orderId);
        setPrevScans([code]);
        setScanedItemsIds(response.data.scanedItemsIds);

        const uniqueItemsIds = response.data.allItemsIds.filter(
          (value, index, self) => self.indexOf(value) === index
        );

        const itemsData = await Promise.all(
          uniqueItemsIds.map(async (itemId) => {
            const itemResponse = await api.get(`/items/${itemId}`);
            const itemName = itemResponse.data.name;
            const pngFileId = itemResponse.data.imageId;

            const pngFileResponse = await api.get(`/PngFiles/${pngFileId}`, { responseType: "blob" });
            const pngFileUrl = URL.createObjectURL(pngFileResponse.data);

            const scannedCount = response.data.scanedItemsIds.filter((id) => id === itemId).length;
            const totalCount = response.data.allItemsIds.filter((id) => id === itemId).length;

            return {
              id: itemId,
              name: itemName,
              imageUrl: pngFileUrl,
              scannedCount,
              totalCount,
            };
          })
        );

        setItems(itemsData);
      } else {
        if (scanedItemsIds.length !== response.data.scanedItemsIds.length) {
          setPrevScans([...prevScans, code]);
        }
        setScanedItemsIds(response.data.scanedItemsIds);

        items.forEach((item) => {
          item.scannedCount = response.data.scanedItemsIds.filter((id) => id === item.id).length;
        });
        setItems([...items]);
      }

      if (response.data.scanCompleted) {
        setIsScanCompleted(true);
      }
    } catch (err) {
      console.error("Ошибка при отправке данных на сервер:", err);
      setError(`Ошибка: ${err.message}`);
    }
  };

  useEffect(() => {
    if (!isScanning) return;

    const scanQR = () => {
      if (videoRef.current && canvasRef.current) {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        const context = canvas.getContext("2d");

        if (video.videoWidth === 0 || video.videoHeight === 0) {
          requestAnimationFrame(scanQR);
          return;
        }

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: "dontInvert" });

        if (code && code.data && code.data.trim() !== "") {
          setResult(code.data);
          handleScannedCode(code.data);
          setIsScanning(false);
        } else {
          requestAnimationFrame(scanQR);
        }
      }
    };

    scanQR();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScanning]);

  const handleResetScanning = () => {
    setIsScanning(false);
    setOrderId(null);
    setPrevScans([]);
    setScanResponse(null);
    setItems([]);
    setError("");
    setIsScanCompleted(false);
    setIsPrintCompleted(false);
  };

  // печать на стороне сервера (как у вас)
  const handlePrint = async () => {
    try {
      await api.post(`/print/${orderId}`);
      setIsPrintCompleted(true);
    } catch (err) {
      console.error("Ошибка при отправке данных на сервер:", err);
      setError(`Ошибка: ${err.message}`);
    }
  };

  const handleComplete = async () => {
    try {
      await api.post(`/orders/CompletePacking/${orderId}`);
    } catch (err) {
      console.error("Ошибка при отправке данных на сервер:", err);
      setError(`Ошибка: ${err.message}`);
      return;
    }
    handleResetScanning();
  };

  return (
    <div>
      {error && <p style={{ color: "red" }}>{error}</p>}

      {/* Блок принтера */}
      {printerAccordion}

      <video
        ref={videoRef}
        muted
        playsInline
        style={{ width: "100%", maxWidth: "500px" }}
        onLoadedMetadata={() => console.log("Видео готово к воспроизведению")}
      />
      <canvas ref={canvasRef} style={{ display: "none" }} />

      {scanResponse && (
        <div>
          <div style={{ display: "flex", overflowX: "auto" }}>
            {items.map((item) => {
              const arr = [];
              for (let i = 0; i < item.totalCount; i++) {
                arr.push(
                  <div
                    key={`${item.id}-${i}`}
                    style={{
                      margin: "5px",
                      border: i < item.scannedCount ? "3px solid green" : "3px solid red",
                      padding: "10px",
                      borderRadius: "5px",
                    }}
                  >
                    <img
                      src={item.imageUrl}
                      alt={item.name}
                      style={{ width: "100px", height: "100px", objectFit: "contain" }}
                    />
                  </div>
                );
              }
              return arr;
            })}
          </div>
          {scanResponse.scanCompleted && <p>Все товары отсканированы!</p>}
        </div>
      )}

      {/* Кнопки управления */}
      <div
        className="d-flex justify-content-around"
        style={{ position: "fixed", bottom: "20px", left: "50%", transform: "translateX(-50%)", width: "100%" }}
      >
        {!isPrintCompleted && (
          <button className="btn btn-lg btn-danger me-2" onClick={handleResetScanning}>
            Сброс
          </button>
        )}

        {hasRole("ScanQrCode") && !isScanCompleted && (
          <button
            onClick={() => setIsScanning(true)}
            className="btn btn-lg btn-primary"
            disabled={isScanning}
          >
            {isScanning ? "Сканирование..." : "Сканировать"}
          </button>
        )}

        {hasRole("PrintSticker") && isScanCompleted && !isPrintCompleted && (
          <button className="btn btn-lg btn-warning me-2" onClick={handlePrint}>
            Печать
          </button>
        )}

        {hasRole("PrintSticker") && isScanCompleted && isPrintCompleted && (
          <button className="btn btn-lg btn-warning me-2" onClick={handlePrint}>
            Печать Еще
          </button>
        )}

        {hasRole("CompletePacking") && isScanCompleted && isPrintCompleted && (
          <button className="btn btn-lg btn-success me-2" onClick={handleComplete}>
            Завершить
          </button>
        )}
      </div>
    </div>
  );
};

export default ScannerTab;
